/*
 * Разделы платформы (пункты меню) и водяные знаки их шапок — вариант 2 макета: шапка темы
 * не меняется (градиент индиго → бирюза с волной; в тёмной теме — графит с мятным
 * свечением), в левом верхнем углу с нахлёстом добавляется знак технологии раздела:
 * белый в светлой теме, цветом раздела (как у карточек шаблонов, lib/templateTypes) в
 * тёмной. Знак — слой background-image (SVG data-URI), поэтому его получают все шапки с
 * themeId раздела: списки разделов, страницы сущностей (themeId = spec.type) и «Создать».
 * Размер и положение знака — переопределение BackstageHeader в теме (WATERMARK).
 */
import { PageTheme, shapes } from '@backstage/theme';
import {
  AIRFLOW_PATHS,
  DEFECTDOJO_PATHS,
  FEAST_PATHS,
  GITLAB_PATHS,
  JUPYTER_PATHS,
  KAFKA_PATHS,
  KUBERNETES_PATHS,
  ML_MODEL_PATHS,
  MONGODB_PATHS,
  POSTGRES_PATHS,
  RABBITMQ_PATHS,
  RUSTFS_PATHS,
  CLOUD_PATHS,
  SERVICE_PATHS,
  VALKEY_PATHS,
  VIVA_MARK,
} from '../components/icons/paths';

export type SectionId =
  | 'home'
  | 'clusters'
  | 'models'
  | 'services'
  | 'jupyterhub'
  | 'feast'
  | 'airflow'
  | 'defectdojo'
  | 'databases'
  | 'brokers'
  | 'mongodb'
  | 'rabbitmq'
  | 'valkey'
  | 'cloudru'
  | 'vkcloud'
  | 'proxmox'
  | 'rustfs'
  | 'gitlab';

/** Знак технологии: контуры 24×24 или знак платформы (штрих). */
type Glyph = { paths: string[] } | 'mark';

export type SectionLook = {
  /** Цвет знака в тёмной теме (в светлой знак белый). */
  color: string;
  glyph: Glyph;
};

export const SECTIONS: Record<SectionId, SectionLook> = {
  home: { color: '#5eead4', glyph: 'mark' },
  clusters: { color: '#3d7ff0', glyph: { paths: KUBERNETES_PATHS } },
  models: { color: '#8b5cf6', glyph: { paths: ML_MODEL_PATHS } },
  services: { color: '#14b8a6', glyph: { paths: SERVICE_PATHS } },
  jupyterhub: { color: '#f47a2d', glyph: { paths: JUPYTER_PATHS } },
  feast: { color: '#f59e0b', glyph: { paths: FEAST_PATHS } },
  airflow: { color: '#17b3a3', glyph: { paths: AIRFLOW_PATHS } },
  defectdojo: { color: '#a78bfa', glyph: { paths: DEFECTDOJO_PATHS } },
  databases: { color: '#4a86b8', glyph: { paths: POSTGRES_PATHS } },
  brokers: { color: '#64748b', glyph: { paths: KAFKA_PATHS } },
  mongodb: { color: '#47a248', glyph: { paths: MONGODB_PATHS } },
  rabbitmq: { color: '#ff6600', glyph: { paths: RABBITMQ_PATHS } },
  valkey: { color: '#6983ff', glyph: { paths: VALKEY_PATHS } },
  cloudru: { color: '#22b8cf', glyph: { paths: CLOUD_PATHS } },
  // VK Cloud — тот же знак облака, синий VK
  vkcloud: { color: '#3d8bff', glyph: { paths: CLOUD_PATHS } },
  // Proxmox VE — знак облака, оранжевый Proxmox
  proxmox: { color: '#e57000', glyph: { paths: CLOUD_PATHS } },
  rustfs: { color: '#38bdf8', glyph: { paths: RUSTFS_PATHS } },
  gitlab: { color: '#fc6d26', glyph: { paths: GITLAB_PATHS } },
};

/** themeId страниц сущностей (spec.type) → раздел: страница кластера в шапке «Кластеров». */
export const SECTION_OF_TYPE: Record<string, SectionId> = {
  'kubernetes-cluster': 'clusters',
  'proxmox-cluster': 'proxmox',
  'ml-model': 'models',
  'app-service': 'services',
  service: 'services',
  jupyterhub: 'jupyterhub',
  feast: 'feast',
  'feature-store': 'feast',
  airflow: 'airflow',
  defectdojo: 'defectdojo',
  'postgres-cluster': 'databases',
  'kafka-cluster': 'brokers',
  'mongodb-cluster': 'mongodb',
  'rabbitmq-cluster': 'rabbitmq',
  'valkey-cluster': 'valkey',
  'managed-postgres': 'cloudru',
  'managed-mysql': 'cloudru',
  'managed-redis': 'cloudru',
  'managed-rabbitmq': 'cloudru',
  'managed-datastore': 'cloudru',
  'vkcloud-postgres': 'vkcloud',
  'vkcloud-mysql': 'vkcloud',
  'vkcloud-redis': 'vkcloud',
  'object-storage': 'rustfs',
  // Resource gitlab (GitLab платформы) и шаблон его обновления (spec.type gitlab)
  gitlab: 'gitlab',
};

/** Остальные themeId Backstage (документация, API, …) — шапка главной. */
export const GENERIC_THEME_IDS = [
  'documentation',
  'tool',
  'website',
  'library',
  'other',
  'app',
  'apis',
  'card',
];

/**
 * Водяной знак: размер и смещение за левый и верхний край шапки (px) — слой 1 в
 * background-image; заголовок отступает от левого края на WATERMARK.textInset.
 */
export const WATERMARK = { size: 260, left: -30, top: -68, textInset: 120 };

/** Прозрачность знака: белый на градиенте светлой темы, цвет раздела на графите. */
const GLYPH_ALPHA = { light: 0.14, dark: 0.3 };

const rgba = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

const svgLayer = (svg: string): string =>
  `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

/** SVG знака как CSS-слой с полупрозрачным цветом — читается как водяной знак. */
export const glyphLayer = (glyph: Glyph, color: string): string => {
  if (glyph === 'mark') {
    const stroke = `stroke='${color}' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'`;
    return svgLayer(
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none'><path d='${VIVA_MARK.first}' ${stroke}/><path d='${VIVA_MARK.second}' ${stroke}/></svg>`,
    );
  }
  const paths = glyph.paths.map(d => `<path d='${d}'/>`).join('');
  return svgLayer(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='${color}'>${paths}</svg>`,
  );
};

/** Шапка темы: градиент светлой темы и свечение тёмной — из токенов темы. */
export type HeaderBase = {
  /** Цвета градиента светлой темы (слева направо). */
  colors: string[];
  fontColor: string;
  /** Тёмная тема: цвет фона и цвет свечения в левом верхнем углу. */
  paper: string;
  glow: string;
};

/**
 * Тема шапки раздела: три слоя background-image в обеих темах — знак, волна (или
 * свечение) и градиент (или сплошной графит); размеры слоёв задаёт переопределение
 * BackstageHeader, поэтому слоёв всегда три.
 */
export const sectionPageTheme = (
  look: SectionLook,
  mode: 'light' | 'dark',
  base: HeaderBase,
): PageTheme => {
  if (mode === 'light') {
    return {
      colors: base.colors,
      shape: shapes.wave,
      backgroundImage: `${glyphLayer(
        look.glyph,
        rgba('#ffffff', GLYPH_ALPHA.light),
      )}, ${shapes.wave}, linear-gradient(90deg, ${base.colors.join(', ')})`,
      fontColor: base.fontColor,
    };
  }
  const glow = `radial-gradient(900px 300px at 20% 0%, ${rgba(
    base.glow,
    0.22,
  )}, transparent 60%)`;
  return {
    colors: [base.paper, base.glow],
    shape: shapes.wave,
    backgroundImage: `${glyphLayer(
      look.glyph,
      rgba(look.color, GLYPH_ALPHA.dark),
    )}, ${glow}, linear-gradient(${base.paper}, ${base.paper})`,
    fontColor: base.fontColor,
  };
};

/** Все themeId темы: разделы, типы сущностей и общие страницы Backstage. */
export const sectionPageThemes = (
  mode: 'light' | 'dark',
  base: HeaderBase,
): Record<string, PageTheme> => {
  const themes: Record<string, PageTheme> = {};
  for (const [id, look] of Object.entries(SECTIONS)) {
    themes[id] = sectionPageTheme(look, mode, base);
  }
  for (const [type, id] of Object.entries(SECTION_OF_TYPE)) {
    themes[type] = themes[id];
  }
  for (const id of GENERIC_THEME_IDS) {
    themes[id] = themes.home;
  }
  return themes;
};
